import React, { Component } from 'react';
import { observer } from 'mobx-react';

import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import Callout from 'core/components/Callout';
import Collapse from 'core/components/Collapse';
import Flex from 'core/components/Flex';
import Box from 'core/components/Box';
import Icon from 'core/components/Icon';
import Text from 'core/components/Text';
import Tag from 'core/components/Tag';
import { formConsumer, Field, InputGroup, Switch } from 'core/form';
import PropTypes from 'prop-types';
import {
  mitigationMethodFieldToMitigationDetailsMap,
  MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP
} from '@kentik/ui-shared/mitigations/constants';

@formConsumer
@observer
export default class FlowSpecIPField extends Component {
  static propTypes = {
    // in case the method has some inferred values, they can be deduced in the context of a wrapping alert object (model)
    alertModel: PropTypes.object,
    // in case the flowspec details are shown in a mitigation context, we derive details from there
    mitigationContext: PropTypes.object,
    // this prop turns the entire component into just an informational panel
    // entirely non-interactional, simply meant to use the complex fields defined in the form structure
    infoMode: PropTypes.bool,
    // this prop makes info mode more compact and on-point
    minimal: PropTypes.bool
  };

  static defaultProps = {
    infoMode: false,
    minimal: false,
    alertModel: null,
    mitigationContext: null
  };

  state = { isOpen: false };

  componentDidMount() {
    const { form, matchFieldType } = this.props;

    this.setState(() => ({ isOpen: form.getValue(`flowspec.${matchFieldType}.enabled`) }));
  }

  onHeaderSwitchClick = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  onEnabledChange = (field, value) => {
    const { form, matchFieldType } = this.props;

    form.setValue(`flowspec.${matchFieldType}.infer`, false);

    this.setState(() => ({ isOpen: value }));
  };

  render() {
    const { form, mitigationContext, alertModel, infoMode, minimal, matchFieldType, onInferChange } = this.props;
    const { isOpen } = this.state;

    const matchFieldEnabledField = form.getField(`flowspec.${matchFieldType}.enabled`);
    const matchFieldEnabledValue = matchFieldEnabledField.getValue();
    const matchFieldInfer = form.getValue(`flowspec.${matchFieldType}.infer`);
    const matchFieldValue = form.getValue(`flowspec.${matchFieldType}.value`);
    const showInferSegment = isOpen && matchFieldInfer;
    const inferField = form.getField(`flowspec.${matchFieldType}.infer`);

    // if open and info mode, condense the data into simple label and value(s)
    // if not open, it means there's no specified value, show a simple label stating so
    if (infoMode) {
      if (minimal && !isOpen) {
        return null;
      }

      return (
        <Flex>
          <Box mr={infoMode ? 0 : '12px'} py="12px" width="100%">
            <Flex flexColumn>
              <Flex justifyContent="space-between" alignItems="center" flex={1}>
                <Text mr={2}>{matchFieldEnabledField.label}:</Text>
                {!isOpen && (
                  <Text as="span" ml={1} color="#6e6e6e" small>
                    No Value Assigned
                  </Text>
                )}
                {isOpen && matchFieldValue && (
                  <Text as="span" ml={1} color="#4a4a4a" small>
                    {matchFieldValue}
                  </Text>
                )}
                {showInferSegment && !(alertModel || mitigationContext) && <Tag ml={1}>Inferred</Tag>}
                {showInferSegment && alertModel && !mitigationContext && (
                  <Flex alignItems="center" justifyContent="center">
                    <Text>Inferred as&nbsp;</Text>
                    <Tag ml={1}>
                      {alertModel.dimensionToKeyPart?.[MITIGATION_METHOD_TO_POLICY_DIMENSION_MAP[matchFieldType]] ??
                        'Unknown Value'}
                    </Tag>
                  </Flex>
                )}
                {showInferSegment && mitigationContext && (
                  <Flex alignItems="center" justifyContent="center">
                    <Text>Inferred as&nbsp;</Text>
                    <Tag ml={1}>
                      {mitigationContext.get('targetObj')?.[
                        mitigationMethodFieldToMitigationDetailsMap[matchFieldType]
                      ] ?? 'Unknown Value'}
                    </Tag>
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Box>
        </Flex>
      );
    }

    const bothSourceAndDestInferred =
      matchFieldType === 'sourceIpCidr' && matchFieldInfer && form.getValue('flowspec.destIpCidr.infer');

    return (
      <Box borderTop="thin" py="12px">
        <Flex justifyContent="space-between">
          <Field
            name={`flowspec.${matchFieldType}.enabled`}
            m={0}
            showLabel={false}
            onClick={this.onHeaderSwitchClick}
            onChange={this.onEnabledChange}
          >
            <Switch showFieldLabel />
          </Field>

          <Field
            name={`flowspec.${matchFieldType}.infer`}
            mb={0}
            showLabel={false}
            showError={false}
            disabled={!matchFieldEnabledValue}
            onClick={this.onHeaderSwitchClick}
            onChange={onInferChange}
          >
            <Switch showFieldLabel />
          </Field>
        </Flex>

        {inferField && inferField.errors && (
          <Flex mt={1}>
            <Text color="danger" small>
              {/* there is only one error ever, and it's about policies using the method that get invalidated if dimension gets inferred */}
              {inferField.errors[0]}
            </Text>
          </Flex>
        )}

        <Collapse isOpen={isOpen}>
          <Flex alignItems="center" p={1}>
            <Field
              name={`flowspec.${matchFieldType}.value`}
              mb={0}
              showLabel={false}
              placeholder="IP/CIDR"
              disabled={!matchFieldEnabledValue || matchFieldInfer}
            >
              <InputGroup />
            </Field>
          </Flex>

          {bothSourceAndDestInferred && (
            <Callout mt="12px" intent="warning">
              <Flex>
                <Icon icon={BsFillExclamationTriangleFill} color="warning" mr={1} />
                <Text as="div">
                  To use this method for a manual mitigation, you may only Infer either Destination IP/CIDR or Source
                  IP/CIDR but not both.
                </Text>
              </Flex>
            </Callout>
          )}
        </Collapse>
      </Box>
    );
  }
}
